import type { IncidentDetailResponse } from '@prerender/diagnostics-api';
import { type ReactNode, useId } from 'react';

import { LastMeasuredNote } from './CrawlerEvidence';
import { formatRequestCount } from './crawlerEvidenceFigures';
import { EVIDENCE_TITLE } from './EvidenceTable';
import { formatRate } from './formatMeasuredValue';
import {
  formatBlockedOfTotal,
  formatMeasurementPeriod,
  type IncidentBlockingFigures,
  measurementPeriodOf,
  type RateRules,
} from './originBlockingFigures';
import SeverityBands from './SeverityBands';

type OriginBlockingEvidenceProps = {
  incident: IncidentDetailResponse;
  figures: IncidentBlockingFigures;
  rules: RateRules | null;
};

type EvidenceFigureProps = {
  name: string;
  help: string;
  className?: string;
  children: ReactNode;
};

function EvidenceFigure({ name, help, className, children }: EvidenceFigureProps) {
  return (
    <div
      className={className === undefined ? 'diagnostics-evidence-figure' : `diagnostics-evidence-figure ${className}`}
      role="group"
      aria-label={name}
    >
      <dt className="diagnostics-evidence-figure-name">{name}</dt>
      <dd className="diagnostics-evidence-figure-value">{children}</dd>
      <dd className="diagnostics-evidence-figure-help">{help}</dd>
    </div>
  );
}

function PeriodLine({ end, windowDays }: { end: string; windowDays: number }) {
  const period = measurementPeriodOf(end, windowDays);

  return (
    <p className="diagnostics-evidence-period">
      {`Last ${windowDays} days · ${formatMeasurementPeriod(period)}`}
    </p>
  );
}

const rateHelpOf = ({ domains, headlineDomain }: IncidentDetailResponse): string =>
  domains.length > 1 ? `Worst domain: ${headlineDomain} — sets the severity` : 'The figure the severity comes from';

const unreadableNoteOf = (count: number): string => `Figures unavailable for ${count} ${count === 1 ? 'domain' : 'domains'}`;

function OriginBlockingEvidence({ incident, figures, rules }: OriginBlockingEvidenceProps) {
  const titleId = useId();

  return (
    <section className="diagnostics-detail-section" aria-labelledby={titleId}>
      <h2 id={titleId} className="diagnostics-detail-section-title">
        {EVIDENCE_TITLE}
      </h2>
      {rules !== null && <PeriodLine end={incident.lastDetectedAt} windowDays={rules.windowDays} />}
      <dl className="diagnostics-evidence-figures">
        <EvidenceFigure
          name="Blocked requests"
          help="Crawler requests answered 403, 503 or 504, of all crawler requests observed"
        >
          {formatBlockedOfTotal(figures)}
        </EvidenceFigure>
        <EvidenceFigure name="Blocked request rate" help={rateHelpOf(incident)}>
          {formatRate(incident.metricValue)}
        </EvidenceFigure>
        <EvidenceFigure
          name="Affected URLs"
          help="Unique URLs that got a 403, 503 or 504. Not a request count; does not set severity"
          className="diagnostics-evidence-figure-apart"
        >
          {formatRequestCount(figures.affectedUrlCount)}
        </EvidenceFigure>
      </dl>
      {figures.lastMeasured && <LastMeasuredNote />}
      {figures.unreadableDomainCount > 0 && (
        <p className="diagnostics-evidence-note">{unreadableNoteOf(figures.unreadableDomainCount)}</p>
      )}
      {rules !== null && (
        <SeverityBands rules={rules} severity={incident.severity} rate={formatRate(incident.metricValue)} />
      )}
    </section>
  );
}

export default OriginBlockingEvidence;
